import React, { useState, useEffect } from 'react';
import styles from './SchedulingFlow.module.css';
import { AxisBrandLogo, axisBranding } from './branding';
// Provider image imports
import heatherMarkhamImage from './SVGs/providerimages/Heather_Markham 2.png';
import kaylaSharpeImage from './SVGs/providerimages/Kayla_Sharpe.png';
import benEgbersImage from './SVGs/providerimages/Ben_Egbers_Boulder_Psychiatrist.png';
import katharineChurchImage from './SVGs/providerimages/Katharine_Selina_Church.png';
import tiffanyWalkerImage from './SVGs/providerimages/Tiffany_Walker.png';
import elinorObuckleyImage from './SVGs/providerimages/Elinor-Obuckley.png';
import sarahMclaughlinImage from './SVGs/providerimages/sarah-mclaughlin.png';
import francesRhodesImage from './SVGs/providerimages/frances-borger-rhodes.png';
import andrewWennerImage from './SVGs/providerimages/Westminster-psychiatrist-Andrew-Wenner-Axis-Integrated-Mental-Health.png';
import madisonWalterImage from './SVGs/providerimages/Madison-Walter.png';
import jamieTeunisImage from './SVGs/providerimages/Jamie-teunis.png';
import rossVanAllenImage from './SVGs/providerimages/Ross-van-allen.png';
import victoriaKauseImage from './SVGs/providerimages/Victoria-Kause.png';
import michelleGodwinImage from './SVGs/providerimages/Michelle-Godwin.png';
import roderickObrienImage from './SVGs/providerimages/dr-roderick-obrien-cropped.jpeg';

// Provider image imports

interface Provider {
  providerId: string;
  departmentId: string;
  firstName: string;
  lastName: string;
  departmentName: string;
  departmentAddress: string;
  schedulingName?: string;
  availableSlots: Array<{
    appointmentId?: string;
    startTime: string;
    endTime: string;
    appointmentTypeId: string;
    appointmentName?: string;
  }>;
}

interface SelectedAppointment {
  provider: Provider;
  slot: Provider['availableSlots'][number];
}

interface ProviderSelectionStepProps {
  onBack: () => void;
  onSelect: (provider: Provider, slot: any) => void;
  onNewSearch: () => void;
  providers: Provider[];
  isLoading: boolean;
  error: string | null;
  reasonType?: 'existing' | 'new' | null;
  isRestrictedProvider?: boolean;
  currentStartDate: Date;
  onNextWeek: () => void;
  onPreviousWeek: () => void;
  multiSelectEnabled?: boolean;
  selectedBulkAppointments?: SelectedAppointment[];
  onBulkSelectionChange?: (items: SelectedAppointment[]) => void;
  onBulkProceed?: (items: SelectedAppointment[]) => void;
}

const ProviderSelectionStep: React.FC<ProviderSelectionStepProps> = React.memo(({
  onBack,
  onSelect,
  onNewSearch,
  providers,
  isLoading,
  error,
  reasonType,
  isRestrictedProvider,
  currentStartDate,
  onNextWeek,
  onPreviousWeek,
  multiSelectEnabled = false,
  selectedBulkAppointments = [],
  onBulkSelectionChange,
  onBulkProceed
}) => {
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [activeDayByProvider, setActiveDayByProvider] = useState<Record<string, string>>({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasNavigatedFromInitial, setHasNavigatedFromInitial] = useState(false);
  const [bulkSelection, setBulkSelection] = useState<SelectedAppointment[]>(selectedBulkAppointments);

  // Show restricted message for restricted providers when reasonType is 'new'
  const shouldShowRestrictedMessage = isRestrictedProvider && reasonType === 'new';

  useEffect(() => {
    setBulkSelection(selectedBulkAppointments);
  }, [selectedBulkAppointments]);

  // Function to get provider image based on provider name or ID
  const getProviderImage = (provider: Provider): string | undefined => {
    const fullName = `${provider.firstName} ${provider.lastName}`.toLowerCase();
    const schedulingName = provider.schedulingName?.toLowerCase() || '';
    
    // Map providers to their images based on name matching
    // Primary matching by full name
    if (fullName.includes('heather') && fullName.includes('markham')) {
      return heatherMarkhamImage;
    }
    if (fullName.includes('kayla') && fullName.includes('sharpe')) {
      return kaylaSharpeImage;
    }
    if (fullName.includes('benjamin') && fullName.includes('egbers')) {
      return benEgbersImage;
    }
    if (fullName.includes('katharine') && fullName.includes('church')) {
      return katharineChurchImage;
    }
    if (fullName.includes('tiffany') && (fullName.includes('walker') || fullName.includes('lawrence'))) {
      return tiffanyWalkerImage;
    }
    if (fullName.includes('elinor') && fullName.includes('obuckley')) {
      return elinorObuckleyImage;
    }
    if (fullName.includes('sarah') && fullName.includes('mclaughlin')) {
      return sarahMclaughlinImage;
    }
    if (fullName.includes('frances') && fullName.includes('rhodes')) {
      return francesRhodesImage;
    }
    if (fullName.includes('andrew') && fullName.includes('wenner')) {
      return andrewWennerImage;
    }
    if (fullName.includes('madison') && fullName.includes('walter')) {
      return madisonWalterImage;
    }
    if (fullName.includes('jamie') && fullName.includes('teunis')) {
      return jamieTeunisImage;
    }
    if (fullName.includes('ross') && fullName.includes('van') && fullName.includes('allen')) {
      return rossVanAllenImage;
    }
    if (fullName.includes('victoria') && fullName.includes('kause')) {
      return victoriaKauseImage;
    }
    if (fullName.includes('michelle') && fullName.includes('godwin')) {
      return michelleGodwinImage;
    }
    if (fullName.includes('roderick') && fullName.includes('obrien')) {
      return roderickObrienImage;
    }
    
    // Fallback matching by scheduling name
    if (schedulingName.includes('heather_markham')) {
      return heatherMarkhamImage;
    }
    if (schedulingName.includes('kayla_sharpe')) {
      return kaylaSharpeImage;
    }
    if (schedulingName.includes('benjamin_egbers') || schedulingName.includes('dr_benjamin_egbers')) {
      return benEgbersImage;
    }
    if (schedulingName.includes('katharine_church')) {
      return katharineChurchImage;
    }
    if (schedulingName.includes('tiffany_walker') || schedulingName.includes('tiffany_lawrence')) {
      return tiffanyWalkerImage;
    }
    if (schedulingName.includes('elinor_obuckley')) {
      return elinorObuckleyImage;
    }
    if (schedulingName.includes('sarah_mclaughlin')) {
      return sarahMclaughlinImage;
    }
    if (schedulingName.includes('frances_rhodes')) {
      return francesRhodesImage;
    }
    if (schedulingName.includes('andrew_wenner')) {
      return andrewWennerImage;
    }
    if (schedulingName.includes('madison_walter')) {
      return madisonWalterImage;
    }
    if (schedulingName.includes('jamie_teunis')) {
      return jamieTeunisImage;
    }
    if (schedulingName.includes('ross_van_allen')) {
      return rossVanAllenImage;
    }
    if (schedulingName.includes('victoria_kause')) {
      return victoriaKauseImage;
    }
    if (schedulingName.includes('michelle_godwin')) {
      return michelleGodwinImage;
    }
    if (schedulingName.includes('roderick_obrien')) {
      return roderickObrienImage;
    }
    
    // Default fallback image (Heather's image as generic placeholder)
    return heatherMarkhamImage;
  };

  // Header/Nav helpers (same as other screens)
  const NAV_PHONE = axisBranding.phoneNumber;
  const NAV_LINKS = [
    { label: 'HOME', href: '#', action: 'home' },
  ];
  const NAV_LINKS_MOBILE = [
    { label: 'HOME', href: '#', action: 'home' },
  ];
  const [isMobile, setIsMobile] = useState(false);
  
  const checkMobile = React.useCallback(() => {
    setIsMobile(window.innerWidth <= 700);
  }, []);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [checkMobile]);

  // Reset navigation state when back to initial week
  useEffect(() => {
    if (isBackToInitialWeek()) {
      setHasNavigatedFromInitial(false);
    }
  }, [currentStartDate]);
  const currentNavLinks = isMobile ? NAV_LINKS_MOBILE : NAV_LINKS;

  const handleNavClick = (action: string) => {
    if (action === 'home') {
      // Reset all data and go to landing
      onNewSearch();
    }
  };

  const buildSlotIdentifier = React.useCallback((slot: Provider['availableSlots'][number], providerId: string) => {
    if (slot?.appointmentId) return slot.appointmentId;
    return `${providerId}-${slot.startTime}-${slot.endTime || ''}`;
  }, []);

  const toggleSlotSelection = React.useCallback((providerItem: Provider, slotItem: Provider['availableSlots'][number]) => {
    const slotId = buildSlotIdentifier(slotItem, providerItem.providerId);
    setBulkSelection(prev => {
      const exists = prev.some(item => buildSlotIdentifier(item.slot, item.provider.providerId) === slotId);
      const updated = exists
        ? prev.filter(item => buildSlotIdentifier(item.slot, item.provider.providerId) !== slotId)
        : [...prev, { provider: providerItem, slot: slotItem }];
      onBulkSelectionChange?.(updated);
      return updated;
    });
  }, [buildSlotIdentifier, onBulkSelectionChange]);

  const clearBulkSelections = React.useCallback(() => {
    setBulkSelection([]);
    onBulkSelectionChange?.([]);
  }, [onBulkSelectionChange]);

  const selectedCount = bulkSelection.length;

  const handleLogoClick = () => {
    window.open(axisBranding.websiteUrl, '_blank');
  };

  const handleNextWeek = () => {
    setIsLoadingMore(true);
    setHasNavigatedFromInitial(true);
    onNextWeek();
    // Reset loading state after a short delay
    setTimeout(() => setIsLoadingMore(false), 1000);
  };

  const handlePreviousWeek = () => {
    setIsLoadingMore(true);
    onPreviousWeek();
    // Reset loading state after a short delay
    setTimeout(() => setIsLoadingMore(false), 1000);
  };

  // Check if we're back to the initial week (today + 2 days)
  const isBackToInitialWeek = () => {
    const today = new Date();
    const initialDate = new Date(today);
    initialDate.setDate(today.getDate() + 2);
    
    // Compare dates (ignore time)
    const currentDateOnly = new Date(currentStartDate.getFullYear(), currentStartDate.getMonth(), currentStartDate.getDate());
    const initialDateOnly = new Date(initialDate.getFullYear(), initialDate.getMonth(), initialDate.getDate());
    
    return currentDateOnly.getTime() === initialDateOnly.getTime();
  };

  const formatDate = (dateString: string) => {
    try {
      const parts = dateString.split('T');
      if (parts.length === 2) {
        const datePart = parts[0]; // "09/08/2025"
        const timePart = parts[1]; // "08:00"
        const [month, day, year] = datePart.split('/');
        const isoDateString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart}:00`;
        return new Date(isoDateString);
      }
      return new Date(dateString);
    } catch (error) {
      // Fallback to current date if parsing fails
      return new Date();
    }
  };

  const formatSelectedSlot = (slot: Provider['availableSlots'][number]) => {
    const date = formatDate(slot.startTime);
    if (Number.isNaN(date.getTime())) {
      return slot.startTime;
    }
    const dateLabel = date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
    const timeLabel = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    return `${dateLabel} • ${timeLabel}`;
  };

  const handleProceed = () => {
    if (multiSelectEnabled) {
      if (bulkSelection.length > 0) {
        onBulkProceed?.(bulkSelection);
      }
      return;
    }
    if (selectedProvider && selectedSlot) {
      onSelect(selectedProvider, selectedSlot);
    }
  };

  const canProceed = multiSelectEnabled ? bulkSelection.length > 0 : Boolean(selectedProvider && selectedSlot);

  const groupSlotsByDay = (slots: Provider['availableSlots']) => {
    const map: Record<string, Provider['availableSlots']> = {};
    for (const s of slots) {
      const d = formatDate(s.startTime);
      const label = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
      if (!map[label]) map[label] = [];
      map[label].push(s);
    }
    return map;
  };

  if (isLoading) {
    return (
      <div className={styles.landingRoot}>
        {/* Top Banner with Background Image and Overlay */}
        <div className={styles.topBanner}>
          <img src={require('./SVGs/image001.png')} alt="Banner" className={styles.bannerImg} />
          <div className={styles.bannerOverlay} />
          {/* Nav Bar */}
          <div className={styles.navBar}>
            <AxisBrandLogo
              variant="white"
              size="extra-large"
              className={styles.navLogo}
              onClick={handleLogoClick}
              style={{ cursor: 'pointer' }}
            />
            <span className={styles.navPhone}>{NAV_PHONE}</span>
            <div className={styles.navLinks}>
              {currentNavLinks.map(link => (
                <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
              ))}
            </div>
          </div>
          <div className={styles.bannerContent}>
            <h1 className={styles.bannerTitle}>Book Online</h1>
            <div className={styles.bannerSubtitle}>
              Schedule your mental health appointment with our qualified providers. Choose between telehealth or in-person visits.
            </div>
          </div>
        </div>

        {/* Main Content with Loading State */}
        <div className={styles.landingMain}>
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}>
              <div className={styles.spinner}></div>
            </div>
            <h2 className={styles.loadingTitle}>Loading Available Providers...</h2>
            <p className={styles.loadingSubtitle}>Please wait while we fetch available appointment slots.</p>
          </div>
        </div>

        {/* Footer */}
        <footer className={styles.landingFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerTitle}>{axisBranding.organizationName}</div>
            <div className={styles.footerContact}>Phone (appointments): {axisBranding.phoneNumber}</div>
            <div className={styles.footerRating}>
              <span className={styles.footerStars}>★★★★★</span>
              <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.landingRoot}>
        {/* Top Banner with Background Image and Overlay */}
        <div className={styles.topBanner}>
          <img src={require('./SVGs/image001.png')} alt="Banner" className={styles.bannerImg} />
          <div className={styles.bannerOverlay} />
          {/* Nav Bar */}
          <div className={styles.navBar}>
            <AxisBrandLogo
              variant="white"
              size="extra-large"
              className={styles.navLogo}
              onClick={handleLogoClick}
              style={{ cursor: 'pointer' }}
            />
            <span className={styles.navPhone}>{NAV_PHONE}</span>
            <div className={styles.navLinks}>
              {currentNavLinks.map(link => (
                <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
              ))}
            </div>
          </div>
          <div className={styles.bannerContent}>
            <h1 className={styles.bannerTitle}>Book Online</h1>
            <div className={styles.bannerSubtitle}>
              Schedule your mental health appointment with our qualified providers. Choose between telehealth or in-person visits.
            </div>
          </div>
        </div>

        {/* Main Content with Error State */}
        <div className={styles.landingMain}>
          <div className={styles.errorContainer}>
            <h2 className={styles.errorTitle}>Error Loading Providers</h2>
            <p className={styles.errorSubtitle}>{error}</p>
            <button onClick={onBack} className={styles.backButton}>
              ← Back
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer className={styles.landingFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerTitle}>{axisBranding.organizationName}</div>
            <div className={styles.footerContact}>Phone (appointments): {axisBranding.phoneNumber}</div>
            <div className={styles.footerRating}>
              <span className={styles.footerStars}>★★★★★</span>
              <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Show "No Appointment Found" state when providers array is empty (but not for restricted providers)
  if (!isLoading && !error && (!providers || providers.length === 0) && !shouldShowRestrictedMessage) {
    return (
      <div className={styles.landingRoot}>
        {/* Top Banner with Background Image and Overlay */}
        <div className={styles.topBanner}>
          <img src={require('./SVGs/image001.png')} alt="Banner" className={styles.bannerImg} />
          <div className={styles.bannerOverlay} />
          {/* Nav Bar */}
          <div className={styles.navBar}>
            <AxisBrandLogo
              variant="white"
              size="extra-large"
              className={styles.navLogo}
              onClick={handleLogoClick}
              style={{ cursor: 'pointer' }}
            />
            <span className={styles.navPhone}>{NAV_PHONE}</span>
            <div className={styles.navLinks}>
              {currentNavLinks.map(link => (
                <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
              ))}
            </div>
          </div>
          <div className={styles.bannerContent}>
            <h1 className={styles.bannerTitle}>Book Online</h1>
            <div className={styles.bannerSubtitle}>
              Schedule your mental health appointment with our qualified providers. Choose between telehealth or in-person visits.
            </div>
          </div>
        </div>

        {/* Main Content with No Appointments State */}
        <div className={styles.landingMain}>
          <div className={styles.noAppointmentsContainer}>
            <div className={styles.noAppointmentsIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#6b7280" strokeWidth="2"/>
                <path d="M15 9l-6 6M9 9l6 6" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className={styles.noAppointmentsTitle}>No Appointments Found</h3>
            <p className={styles.noAppointmentsSubtitle}>
              We couldn't find any available appointments matching your search criteria. 
              Please try adjusting your search parameters or contact us for assistance.
            </p>
            <div className={styles.helpStatement}>
              Need assistance sooner? Telehealth appointments are typically available within 7 days. In-person availability may vary by location and can take longer.
            </div>
            <div className={styles.noAppointmentsActions}>
              <button onClick={onBack} className={styles.backButton}>
                ← Back
              </button>
              <button onClick={onNewSearch} className={styles.primaryButton}>
                New Search
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className={styles.landingFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerTitle}>{axisBranding.organizationName}</div>
            <div className={styles.footerContact}>Phone (appointments): {axisBranding.phoneNumber}</div>
            <div className={styles.footerRating}>
              <span className={styles.footerStars}>★★★★★</span>
              <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Show restricted provider message when no providers and it's a restricted provider
  if (!isLoading && !error && (!providers || providers.length === 0) && shouldShowRestrictedMessage) {
    return (
      <div className={styles.landingRoot}>
        {/* Top Banner with Background Image and Overlay */}
        <div className={styles.topBanner}>
          <img src={require('./SVGs/image001.png')} alt="Banner" className={styles.bannerImg} />
          <div className={styles.bannerOverlay} />
          {/* Nav Bar */}
          <div className={styles.navBar}>
            <AxisBrandLogo
              variant="white"
              size="extra-large"
              className={styles.navLogo}
              onClick={handleLogoClick}
              style={{ cursor: 'pointer' }}
            />
            <span className={styles.navPhone}>{NAV_PHONE}</span>
            <div className={styles.navLinks}>
              {currentNavLinks.map(link => (
                <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
              ))}
            </div>
          </div>
          <div className={styles.bannerContent}>
            <h1 className={styles.bannerTitle}>Book Online</h1>
            <div className={styles.bannerSubtitle}>
              Schedule your mental health appointment with our qualified providers. Choose between telehealth or in-person visits.
            </div>
          </div>
        </div>

        {/* Main Content with Restricted Provider Message */}
        <div className={styles.landingMain}>
          {/* Restricted provider message with better spacing */}
          <div className={styles.restrictedMessageContainer}>
            <div className={styles.restrictedMessage}>
              <div className={styles.restrictedIcon}>⚠️</div>
              <div className={styles.restrictedText}>
                This provider is not accepting new patients.
              </div>
            </div>
          </div>

          {/* Action buttons with better spacing */}
          <div className={styles.restrictedActions}>
            <button onClick={onBack} className={styles.backButton}>
              ← Back
            </button>
            <button onClick={onNewSearch} className={styles.primaryButton}>
              New Search
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer className={styles.landingFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerTitle}>{axisBranding.organizationName}</div>
            <div className={styles.footerContact}>Phone (appointments): {axisBranding.phoneNumber}</div>
            <div className={styles.footerRating}>
              <span className={styles.footerStars}>★★★★★</span>
              <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className={styles.landingRoot}>
      {/* Top Banner with Background Image and Overlay */}
      <div className={styles.topBanner}>
        <img src={require('./SVGs/image001.png')} alt="Banner" className={styles.bannerImg} />
        <div className={styles.bannerOverlay} />
        {/* Nav Bar */}
        <div className={styles.navBar}>
          <AxisBrandLogo
            variant="white"
            size="extra-large"
            className={styles.navLogo}
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          />
          <span className={styles.navPhone}>{NAV_PHONE}</span>
          <div className={styles.navLinks}>
            {currentNavLinks.map(link => (
              <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
            ))}
          </div>
        </div>
        <div className={styles.bannerContent}>
          <h1 className={styles.bannerTitle}>Book Online</h1>
          <div className={styles.bannerSubtitle}>
            Schedule your mental health appointment with our qualified providers. Choose between telehealth or in-person visits.
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.landingMain}>
        <div className={styles.landingHeader}>
          <h2 className={styles.landingTitle}>Choose your Provider</h2>
          <div className={styles.landingSubtitle}>Select a provider for your appointment</div>
          
          {/* Pagination Controls */}
          <div className={styles.paginationControls}>
            {hasNavigatedFromInitial && !isBackToInitialWeek() && (
              <button 
                className={styles.paginationButton}
                onClick={handlePreviousWeek}
                disabled={isLoadingMore || isLoading}
              >
                {isLoadingMore ? 'Loading...' : '← Previous Week'}
              </button>
            )}
            <span className={styles.paginationInfo}>
              Week of {currentStartDate.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </span>
            <button 
              className={styles.paginationButton}
              onClick={handleNextWeek}
              disabled={isLoadingMore || isLoading}
            >
              {isLoadingMore ? 'Loading...' : 'Next Week →'}
            </button>
          </div>
        </div>

        {/* Restricted provider message */}
        {shouldShowRestrictedMessage && (
          <div className={styles.restrictedMessage}>
            <div className={styles.restrictedIcon}>⚠️</div>
            <div className={styles.restrictedText}>
              This provider is not accepting new patients.
            </div>
          </div>
        )}

        {multiSelectEnabled && (
          <div className={styles.bulkSelectionBanner}>
            <div className={styles.bulkSelectionInfo}>
              {selectedCount > 0
                ? `Selected ${selectedCount} appointment${selectedCount > 1 ? 's' : ''}.`
                : 'Select one or more Spravato appointments to continue.'}
            </div>
            {selectedCount > 0 && (
              <button
                type="button"
                className={styles.bulkSelectionClear}
                onClick={clearBulkSelections}
              >
                Clear all
              </button>
            )}
          </div>
        )}

        <div className={styles.providersContainer}>
        {providers.map((provider) => {
          const grouped = groupSlotsByDay(provider.availableSlots || []);
          const dayLabels = Object.keys(grouped);
          const providerKey = provider.providerId;
          const activeDay = activeDayByProvider[providerKey] || dayLabels[0] || '';
          const providerSelections = multiSelectEnabled
            ? bulkSelection.filter(item => item.provider.providerId === provider.providerId)
            : [];

          return (
            <div
              key={provider.providerId}
              className={styles.providerCard}
            >
              <div className={styles.providerHeader}>
                <div className={styles.providerIcon}>
                  {(() => {
                    const imageSrc = getProviderImage(provider);
                    return imageSrc ? (
                      <img 
                        src={imageSrc} 
                        alt={`${provider.firstName} ${provider.lastName}`}
                        className={styles.providerImage}
                      />
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    );
                  })()}
                </div>
                <div className={styles.providerInfo}>
                  <h3 className={styles.providerName}>
                    {provider.firstName} {provider.lastName}
                  </h3>
                  <div className={styles.providerLocation}>
                    {provider.departmentAddress || provider.departmentName}
                  </div>
                </div>
              </div>

              <div className={styles.slotsContainer}>
                <h4 className={styles.slotsTitle}>Available Appointments</h4>

                {/* Day Tabs - Always show even for single day */}
                {dayLabels.length > 0 && (
                  <div className={styles.dayTabs}>
                    {dayLabels.map((label) => (
                      <button
                        key={label}
                        className={`${styles.dayTab} ${label === activeDay ? styles.dayTabActive : ''}`}
                        onClick={() => setActiveDayByProvider(prev => ({ ...prev, [providerKey]: label }))}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                <div className={styles.slotsGrid}>
                  {(grouped[activeDay] || provider.availableSlots || []).map((slot, index) => {
                    const slotId = buildSlotIdentifier(slot, provider.providerId);
                    const isSelected = multiSelectEnabled
                      ? bulkSelection.some(item => buildSlotIdentifier(item.slot, item.provider.providerId) === slotId)
                      : (selectedProvider?.providerId === provider.providerId &&
                        selectedSlot &&
                        buildSlotIdentifier(selectedSlot, selectedProvider.providerId) === slotId);

                    return (
                    <button
                      key={index}
                      className={`${styles.slotButton} ${isSelected ? styles.selectedSlot : ''}`}
                      onClick={() => {
                        if (multiSelectEnabled) {
                          toggleSlotSelection(provider, slot);
                        } else {
                          setSelectedProvider(provider);
                          setSelectedSlot(slot);
                        }
                      }}
                    >
                      {formatDate(slot.startTime)
                        .toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })
                        .toLowerCase()}
                    </button>
                  );})}
                </div>
              </div>

              {multiSelectEnabled && providerSelections.length > 0 && (
                <div className={styles.selectedAppointments}>
                  <div className={styles.selectedAppointmentsTitle}>Selected for this provider:</div>
                  <div className={styles.selectedAppointmentsChips}>
                    {providerSelections.map((item, index) => {
                      const label = formatSelectedSlot(item.slot);
                      return (
                        <button
                          key={`${item.slot.appointmentId || index}-${label}`}
                          type="button"
                          className={styles.selectedAppointmentChip}
                          onClick={() => toggleSlotSelection(item.provider, item.slot)}
                        >
                          {label}
                          <span className={styles.selectedAppointmentRemove}>×</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </div>

        {multiSelectEnabled && (
          <div className={styles.bulkSelectionSummary}>
            {bulkSelection.length > 0
              ? `Selected ${bulkSelection.length} appointment${bulkSelection.length > 1 ? 's' : ''}.`
              : 'Select one or more Spravato appointments to continue.'}
          </div>
        )}

        <div className={styles.actions}>
          <button onClick={onBack} className={styles.backButton}>
            ← Back
          </button>
          <button
            onClick={handleProceed}
            className={`${styles.primaryButton} ${!canProceed ? styles.disabled : ''}`}
            disabled={!canProceed}
          >
            Continue
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className={styles.landingFooter}>
        <div className={styles.footerContent}>
          <div className={styles.footerTitle}>{axisBranding.organizationName}</div>
          <div className={styles.footerContact}>Phone (appointments): {axisBranding.phoneNumber}</div>
          <div className={styles.footerRating}>
            <span className={styles.footerStars}>★★★★★</span>
            <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
          </div>
        </div>
      </footer>
    </div>
  );
});

export default ProviderSelectionStep;